import { useAlerts } from "components/Alerts/AlertProvider";
import { JobStatus, JobTask } from "generated-graphql/graphql";
import { useToken } from "providers/token";
import { useCallback, useEffect, useState } from "react";
import { useCheckForFile } from "./useCheckForFile";
import { useLongPollingForJob } from "./useLongPollingForJob";

export interface ExportParams {
  tenantId?: string;
  fileName?: string;
  facilityId?: string;
  reportingYear?: number | string;
  /** Export type string used for the export endpoint - see
   * `apps/api/src/download/index.ts` */
  exportType: string;
  filter?: Record<string, any>;
  /**
   * Column visibility model from the grid settings.
   * Keys are field names, values are booleans (false = hidden).
   * Used to filter which columns are included in the export.
   */
  columnVisibility?: Record<string, boolean>;
}

export type ExportStatus = "idle" | "loading" | "ready_to_download";

export interface UseExportDataReturn {
  startExport: (params: ExportParams, onComplete?: () => void) => Promise<void>;
  downloadFile: () => void;
  clearCache: () => void;
  status: ExportStatus;
}

export function generateSpreadsheetFilename(prefix: string): string {
  return `${prefix.replace(/[&]+/g, "")}-${new Date().toISOString()}.xlsx`;
}

// Hook for caching export results, so that subsequent clicks on the same export button don't trigger a new export
const useExportCache = () => {
  const [cachedExports, setCachedExports] = useState<Map<string, string>>(
    new Map()
  );

  // Generate a cache key from export parameters
  const generateCacheKey = useCallback((params: ExportParams) => {
    // Create a deterministic key from the parameters
    const keyData = {
      exportType: params.exportType,
      tenantId: params.tenantId,
      facilityId: params.facilityId,
      reportingYear: params.reportingYear,
      filter: params.filter,
      columnVisibility: params.columnVisibility,
      // Note: We don't include fileName in cache key since it contains timestamp
    };
    return JSON.stringify(keyData);
  }, []);

  const getCachedExport = useCallback(
    (params: ExportParams) => {
      const cacheKey = generateCacheKey(params);
      return cachedExports.get(cacheKey);
    },
    [cachedExports, generateCacheKey]
  );

  const setCachedExport = useCallback(
    (params: ExportParams, signedUrl: string) => {
      const cacheKey = generateCacheKey(params);
      setCachedExports((prev) => new Map(prev.set(cacheKey, signedUrl)));
    },
    [generateCacheKey]
  );

  const clearCache = useCallback(() => {
    setCachedExports(new Map());
  }, []);

  return {
    getCachedExport,
    setCachedExport,
    clearCache,
  };
};

/**
 * This hook helps with generating of export files from the server.
 */
export const useExportData = ({
  timeoutInSeconds,
  autoDownload = true,
}: {
  timeoutInSeconds?: number;
  autoDownload?: boolean;
}): UseExportDataReturn => {
  const { getIdTokenRefreshIfExpired } = useToken();
  const alerts = useAlerts();
  const [status, setStatus] = useState<ExportStatus>("idle");
  const [signedUrl, setSignedUrl] = useState<string | undefined>(undefined);
  const [onCompleteCallback, setOnCompleteCallback] = useState<
    (() => void) | undefined
  >(undefined);
  const [currentParams, setCurrentParams] = useState<ExportParams | undefined>(
    undefined
  );

  const { polling, result, startPolling } = useLongPollingForJob({
    timeoutInSeconds: timeoutInSeconds ?? 900, // 15 minutes
  });

  const { fetchFile } = useCheckForFile();
  const { getCachedExport, setCachedExport, clearCache } = useExportCache();

  // Generate endpoint URL for a given export job
  const generateEndpoint = useCallback((params: ExportParams) => {
    const fullFileName = generateSpreadsheetFilename(
      params.fileName ?? params.exportType
    );
    const urlParts = [
      `/download/${params.exportType}?filename=${encodeURIComponent(fullFileName)}`,
      params.tenantId ? `&tenantId=${params.tenantId.trim()}` : "",
      params.facilityId ? `&facilityId=${params.facilityId.trim()}` : "",
      params.reportingYear ? `&reportingYear=${params.reportingYear}` : "",
      params.filter
        ? `&filter=${encodeURIComponent(JSON.stringify(params.filter))}`
        : "",
      params.columnVisibility
        ? `&columnVisibility=${encodeURIComponent(JSON.stringify(params.columnVisibility))}`
        : "",
    ];
    return urlParts.join("");
  }, []);

  // Handle job completion from polling
  const handleJobComplete = useCallback(
    async (params: ExportParams) => {
      if (result.pollingTimedOut) {
        setStatus("idle");
        alerts.error("Export job timed out");
        return;
      }

      if (!result.job) {
        return;
      }

      if (result.job.status === JobStatus.Succeeded) {
        const fileSignedUrl = await fetchFile(
          result.job.output.downloadKeys[0]
        );

        if (fileSignedUrl) {
          setSignedUrl(fileSignedUrl);
          setStatus("ready_to_download");

          // Cache the result for future use
          setCachedExport(params, fileSignedUrl);

          // Auto-download if enabled
          if (autoDownload) {
            // Trigger download immediately
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = fileSignedUrl;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Reset state after auto-download
            setStatus("idle");
            setSignedUrl(undefined);
            setOnCompleteCallback(undefined);
            setCurrentParams(undefined);
          }

          // Call the onComplete callback if provided
          if (onCompleteCallback) {
            onCompleteCallback();
          }
        } else {
          setStatus("idle");
          alerts.error("Failed to generate export file");
        }
      } else if (result.job.status === JobStatus.Failed) {
        setStatus("idle");
        const actionName =
          result.job.task === JobTask.GeneratePdf ? "PDF" : "export file";
        alerts.error(`Failed to generate ${actionName}`);
      }
    },
    [
      result,
      alerts,
      fetchFile,
      onCompleteCallback,
      setCachedExport,
      autoDownload,
    ]
  );

  // Handle job completion when polling result changes
  useEffect(() => {
    if (result.job && currentParams && !polling) {
      handleJobComplete(currentParams);
    }
  }, [result, polling, currentParams, handleJobComplete]);

  const startExport = useCallback(
    async (params: ExportParams, onComplete?: () => void) => {
      const shouldAutoDownload = autoDownload;

      // Check if we have a cached export for these parameters (unless forcing new)
      const cachedSignedUrl = getCachedExport(params);
      if (cachedSignedUrl) {
        // Use cached result
        setSignedUrl(cachedSignedUrl);
        setStatus("ready_to_download");
        setOnCompleteCallback(() => onComplete);

        // Auto-download from cache if enabled
        if (shouldAutoDownload) {
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = cachedSignedUrl;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          // Reset state after auto-download
          setStatus("idle");
          setSignedUrl(undefined);
          setOnCompleteCallback(undefined);
          setCurrentParams(undefined);
        }

        // Call the onComplete callback if provided
        if (onComplete) {
          onComplete();
        }
        return;
      }

      // Reset state for new export
      setStatus("loading");
      setSignedUrl(undefined);
      setOnCompleteCallback(() => onComplete);
      setCurrentParams(params);

      const apiUrl = import.meta.env.VITE_API_URL;
      const idToken = await getIdTokenRefreshIfExpired();
      const authorization = `Bearer ${idToken || ""}`;
      const endpoint = generateEndpoint(params);

      try {
        const response = await fetch(`${apiUrl}${endpoint}`, {
          headers: {
            authorization,
          },
        });

        if (response.status >= 400) {
          setStatus("idle");
          alerts.error("Error generating export file");
        } else {
          const { jobId: serverJobId } = await response.json();
          startPolling(serverJobId);
        }
      } catch (err) {
        console.error(err);
        setStatus("idle");
        alerts.error("Error generating export file");
      }
    },
    [
      getIdTokenRefreshIfExpired,
      alerts,
      generateEndpoint,
      startPolling,
      getCachedExport,
      autoDownload,
    ]
  );

  const downloadFile = useCallback(() => {
    if (!signedUrl) return;

    const a = document.createElement("a");
    a.style.display = "none";
    a.href = signedUrl;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Reset state after download
    setStatus("idle");
    setSignedUrl(undefined);
    setOnCompleteCallback(undefined);
    setCurrentParams(undefined);
  }, [signedUrl]);

  return {
    startExport,
    downloadFile,
    clearCache,
    status,
  };
};
